import { useEffect, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  FileText,
  History,
  LayoutGrid,
  Package2,
  Plus,
  ShoppingCart,
} from 'lucide-react';

export type InventoryNavItem = 'inventory' | 'orders' | 'purchase-orders' | 'transactions';

export type InventoryCreateAction = 'new-item' | 'order' | 'purchase-order' | 'receive' | 'withdraw';

interface InventoryBottomNavProps {
  /** Which bottom item should render as active (null = none, e.g. a ⋯-menu view). */
  active: InventoryNavItem | null;
  onInventory: () => void;
  onOrders: () => void;
  onPurchaseOrders: () => void;
  onTransactions: () => void;
  onCreateAction: (action: InventoryCreateAction) => void;
}

/**
 * Bottom tab bar for Inventory — five equal tiles:
 * Inventory · Orders · Create · Purchase Orders · Transactions.
 * Create stays a plain tile, same as Marketing. Rentals and Suppliers
 * stay in the top ⋯ menu.
 */
export default function InventoryBottomNav({
  active,
  onInventory,
  onOrders,
  onPurchaseOrders,
  onTransactions,
  onCreateAction,
}: InventoryBottomNavProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!createOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (createRef.current && !createRef.current.contains(t)) setCreateOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [createOpen]);

  const itemClass = (isActive: boolean) =>
    `flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 py-1.5 rounded-xl touch-manipulation transition-colors ${
      isActive ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600 active:text-gray-700'
    }`;

  const labelClass = (isActive: boolean) =>
    `text-[11px] leading-none ${isActive ? 'font-semibold' : 'font-medium'}`;

  return (
    <div
      className="shrink-0 border-t border-gray-200/70 bg-white/95 backdrop-blur-sm z-20"
      style={{
        WebkitBackdropFilter: 'blur(12px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
      }}
    >
      <div className="mx-auto flex w-full max-w-2xl items-stretch justify-between px-2 sm:px-6 py-1.5">
        <button
          type="button"
          onClick={onInventory}
          className={itemClass(active === 'inventory')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <LayoutGrid
            className="h-[22px] w-[22px]"
            strokeWidth={active === 'inventory' ? 2.2 : 1.9}
          />
          <span className={labelClass(active === 'inventory')}>Inventory</span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'inventory' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>

        <button
          type="button"
          onClick={onOrders}
          className={itemClass(active === 'orders')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <ShoppingCart
            className="h-[22px] w-[22px]"
            strokeWidth={active === 'orders' ? 2.2 : 1.9}
          />
          <span className={labelClass(active === 'orders')}>Orders</span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'orders' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>

        <div className="relative flex flex-1 min-w-0" ref={createRef}>
          <button
            type="button"
            onClick={() => setCreateOpen((v) => !v)}
            aria-label="Create"
            aria-expanded={createOpen}
            className={itemClass(createOpen)}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span
              className={`flex h-[22px] w-[22px] items-center justify-center rounded-[6px] border-[1.9px] ${
                createOpen ? 'border-gray-900 bg-gray-900 text-white' : 'border-current'
              }`}
            >
              <Plus
                className={`h-3.5 w-3.5 transition-transform ${createOpen ? 'rotate-45' : ''}`}
                strokeWidth={2.4}
              />
            </span>
            <span className={labelClass(createOpen)}>Create</span>
            <span
              className={`h-[3px] w-5 rounded-full ${createOpen ? 'bg-gray-900' : 'bg-transparent'}`}
              aria-hidden
            />
          </button>

          {createOpen ? (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-60 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden py-1">
              <button
                type="button"
                onClick={() => {
                  setCreateOpen(false);
                  onCreateAction('new-item');
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <Package2 className="h-4 w-4 text-gray-700" />
                </span>
                New item
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreateOpen(false);
                  onCreateAction('order');
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <ShoppingCart className="h-4 w-4 text-gray-700" />
                </span>
                Order
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreateOpen(false);
                  onCreateAction('purchase-order');
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <FileText className="h-4 w-4 text-gray-700" />
                </span>
                Purchase order
              </button>

              {/* Receive + Withdraw share one row to keep the menu compact */}
              <div className="flex">
                <button
                  type="button"
                  onClick={() => {
                    setCreateOpen(false);
                    onCreateAction('receive');
                  }}
                  className="flex flex-1 items-center gap-2.5 pl-4 pr-2 py-2.5 text-left text-[14px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50">
                    <ArrowUp className="h-4 w-4 text-emerald-500" />
                  </span>
                  Receive
                </button>
                <div className="my-2 w-px bg-gray-100" aria-hidden />
                <button
                  type="button"
                  onClick={() => {
                    setCreateOpen(false);
                    onCreateAction('withdraw');
                  }}
                  className="flex flex-1 items-center gap-2.5 pl-3 pr-2 py-2.5 text-left text-[14px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50">
                    <ArrowDown className="h-4 w-4 text-red-400" />
                  </span>
                  Withdraw
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onPurchaseOrders}
          className={itemClass(active === 'purchase-orders')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <FileText
            className="h-[22px] w-[22px]"
            strokeWidth={active === 'purchase-orders' ? 2.2 : 1.9}
          />
          <span className={`${labelClass(active === 'purchase-orders')} text-center leading-tight`}>
            <span className="sm:hidden">Purchases</span>
            <span className="hidden sm:inline">Purchase Orders</span>
          </span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'purchase-orders' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>

        <button
          type="button"
          onClick={onTransactions}
          className={itemClass(active === 'transactions')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <History
            className="h-[22px] w-[22px]"
            strokeWidth={active === 'transactions' ? 2.2 : 1.9}
          />
          <span className={`${labelClass(active === 'transactions')} text-center leading-tight`}>
            Transactions
          </span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'transactions' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
